/* eslint-disable radix */
/* eslint-disable no-plusplus */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
  Button, FormLabel, Grid, makeStyles, TextField
} from '@material-ui/core';
import {
  PersonalInfos, SectionHeader, SectionTitle, Subscription, SubscriptionSummary
} from 'components/Subscription';
import {
  faChevronRight, faPhone, faUser
} from '@fortawesome/pro-regular-svg-icons';
import { faLocationDot } from '@fortawesome/sharp-regular-svg-icons';
import {
  faMinusCircle,
  faPlusCircle
} from '@fortawesome/pro-solid-svg-icons';
import { useFormState } from 'react-use-form-state';
import { FormHelper, translate } from 'utils';
import { FormAddress, FormSelect, PayboxPayment } from 'components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MAGAZINE, PAYMENT_TYPE } from 'utils/constants';
import { SubscriptionService } from 'services';
import { Skeleton } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    width: '100%',
    maxWidth: '100%',
    overflowX: 'hidden',
    boxSizing: 'border-box',
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(6)
    }
  },
  numberOfCopiesSelect: {
    '& input': {
      padding: '12px 4px',
      textAlign: 'center'
    }
  }
}));

const REQUIRED_FIELDS_GLOBAL = [
  'address1',
  'city',
  'civility',
  'country',
  'firstName',
  'lastName',
  'postalCode'
];

const REQUIRED_FIELDS_WITH_EMAIL = [
  'email',
  ...REQUIRED_FIELDS_GLOBAL
];

const PageSubscription = () => {
  const classes = useStyles();
  const [currentSectionOpen, setCurrentSectionOpen] = useState(1);
  const [sectionsAlreadyOpen, setSectionsAlreadyOpen] = useState([1]);
  const [canGoNext, setCanGoNext] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [prices, setPrices] = useState([]);
  const [isLoadingPrices, setIsLoadingPrices] = useState(true);

  const [formState, {
    text, select, raw, email, tel, number, checkbox
  }] = useFormState({
    country: FormHelper.getDefaultCountry(),
    civility: '1',
    magazine: MAGAZINE[0],
    numberOfCopies: 1,
    subscriptions: [
      {
        id: 1,
        npai: 'true',
        country: FormHelper.getDefaultCountry(),
        civility: 1,
        isValid: true
      }
    ],
    conditionsAccepted: false
  });

  const handleSectionOpenChange = newSection => {
    let newSectionValue = newSection;
    if (!newSection) newSectionValue = currentSectionOpen < 3 ? currentSectionOpen + 1 : currentSectionOpen;
    setCurrentSectionOpen(newSectionValue);
    if (!sectionsAlreadyOpen.includes(newSectionValue)) {
      setSectionsAlreadyOpen([...sectionsAlreadyOpen, newSectionValue]);
    }
  };

  useEffect(() => {
    SubscriptionService.getSubscriptionsPrices().then(resp => {
      setPrices(resp.data.filter(d => d.codeRevue !== 'ALLO18ADH'));
    }).finally(() => setIsLoadingPrices(false));
  }, []);

  // Validate steps
  useEffect(() => {
    let canGoNextResult = true;

    switch (currentSectionOpen) {
      case 1:
        REQUIRED_FIELDS_WITH_EMAIL.forEach(field => {
          if (!(!!formState.values[field] && formState.values[field] !== '')) {
            canGoNextResult = false;
          }
        });
        break;

      case 2:
        formState.values.subscriptions.forEach(subscription => {
          if (subscription.values?.npai === 'false') {
            REQUIRED_FIELDS_GLOBAL.forEach(field => {
              if (!(!!subscription.values[field] && subscription.values[field] !== '')
                && (Object.values(subscription.errors).filter(error => error !== undefined).length === 0)) {
                canGoNextResult = false;
              }
            });
          }
        });
        break;

      default:
        break;
    }

    setCanGoNext(
      canGoNextResult &&
      (Object.values(formState.errors).filter(error => error !== undefined).length === 0)
    );
  }, [formState.values]);

  const handleAddSubscription = (n = 1, newCount = formState.values.numberOfCopies) => {
    const referenceId = parseInt(newCount) - n;
    const items = [];

    for (let i = 1; i <= n; i++) {
      items.push({ id: referenceId + i, npai: 'true' });
    }

    formState.setField('subscriptions', [
      ...formState.values.subscriptions,
      ...items
    ]);
  };

  const handleRemoveSubscription = (n = 1) => {
    if (formState.values.subscriptions.length > n) {
      const subs = [...formState.values.subscriptions];
      subs.splice(n * -1);
      formState.setField('subscriptions', [...subs]);
    }
  };

  const handlePlusSubscription = () => {
    formState.setField('numberOfCopies', formState.values.numberOfCopies + 1);
    handleAddSubscription(1, formState.values.numberOfCopies + 1);
  };

  const handleMinusSubscription = () => {
    if (formState.values.numberOfCopies > 1) {
      formState.setField('numberOfCopies', formState.values.numberOfCopies - 1);
      handleRemoveSubscription();
    }
  };

  const createSubscriberAndPaymentInfos = () => {
    // On récupère les abonnements au format valeurs (s'ils viennent de formState)
    const subscriptions = formState.values.subscriptions.map(subscription => {
      const s = subscription.values ?? subscription;

      const adresseBenef = {
        ligne1: s.address1 ?? '',
        ligne2: s.address2 ?? '',
        ligne3: s.address3 ?? '',
        lieuDit: s.lieuDit ?? '',
        codePostal: s.postalCode ?? '',
        ville: s.city ?? '',
        // <-- Normalisation pays en string
        pays: typeof s.country === 'object' ? s.country.label ?? '' : s.country ?? '',
        npai: typeof s.npai === 'boolean' ? s.npai : (s.npai === 'true' || s.npai === true)
      };

      return {
        idRevue: s.idRevue ?? 0,
        nombreDeCopies: s.nombreDeCopies ?? s.numberOfCopies ?? 1,
        nomBeneficiaire: s.nomBeneficiaire ?? s.lastName ?? '',
        prenomBeneficiaire: s.prenomBeneficiaire ?? s.firstName ?? '',
        idCivilite: s.civility ?? 0,
        adresse: adresseBenef
      };
    });

    // Normalisation de l'adresse principale
    const mainAddress = {
      ligne1: formState.values.address1 ?? formState.values.address?.address1 ?? '',
      ligne2: formState.values.address2 ?? formState.values.address?.address2 ?? '',
      ligne3: formState.values.address3 ?? formState.values.address?.address3 ?? '',
      lieuDit: formState.values.address?.lieuDit ?? '',
      codePostal: formState.values.postalCode ?? formState.values.address?.postalCode ?? '',
      ville: formState.values.city ?? formState.values.address?.city ?? '',
      // <-- Normalisation pays en string
      pays: typeof formState.values.country === 'object' ? formState.values.country.label ?? '' : formState.values.country ?? '',
      npai: formState.values.subscriptions?.some(s => s.values?.npai === 'false') ? false : true
    };

    // Contact principal
    const contact = {
      courrielPersonnel: formState.values.email ?? '',
      courrielProfessionnel: formState.values.emailPro ?? '',
      telephoneDomicile: formState.values.phoneHome ?? '',
      telephoneBureau: formState.values.phoneOffice ?? '',
      telephonePortable1: formState.values.phone ?? '',
      telephonePortable2: formState.values.phone2 ?? ''
    };

    // Construction du payload complet attendu par l'API
    const model = {
      tranId: `TMP_${PAYMENT_TYPE.SUBSCRIPTION.code}_${new Date().getTime()}`,
      nom: formState.values.lastName ?? '',
      prenom: formState.values.firstName ?? '',
      idCivilite: parseInt(formState.values.civility ?? formState.values.civility, 10) || 0,
      dateNaissance: formState.values.birthDate ?? null,
      contact,
      adresse: mainAddress,
      abonnementsModel: subscriptions,
      price: totalPrice,
      payload: JSON.stringify({
        price: totalPrice,
        lastName: formState.values.lastName,
        firstName: formState.values.firstName,
        postalCode: formState.values.postalCode,
        email: formState.values.email,
        formStateValues: { ...formState.values }
      })
    };

    localStorage.setItem('subscriber', JSON.stringify({ ...formState.values, subscriptions }));

    return Promise.resolve(model);
  };


  useEffect(() => {
    const diff = formState.values.numberOfCopies - formState.values.subscriptions.length;
    if (diff === 0) return;

    if (diff > 0) handleAddSubscription(diff);
    else handleRemoveSubscription(Math.abs(diff));
  }, [formState.values.numberOfCopies]);

  return (
    <Grid className={classes.container} container direction="column" spacing={4}>


      <Grid item>
        <SectionHeader
          label={translate('subscription.personalInfos')}
          sectionNumber="1"
          onClick={(currentSectionOpen !== 1 && sectionsAlreadyOpen.includes(1)) ? () => handleSectionOpenChange(1) : null}
        />

        {currentSectionOpen === 1 && (
          <Grid container direction="column" spacing={3} style={{ marginTop: 8 }}>

            <Grid item xs={12}>
              <SectionTitle icon={faUser} label={translate('subscription.yourInfos')} />
              <PersonalInfos formState={formState} select={select} text={text} />
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={3} direction="row">
                <Grid item sm={6} xs={12}>
                  <SectionTitle icon={faLocationDot} label={translate('subscription.yourAddress')} />

                  {!isLoadingPrices ? (
                    <FormAddress
                      displayPrice
                      formState={formState}
                      prices={prices}
                      raw={raw}
                      required
                      select={select}
                      text={text}
                      variant="outlined"
                    />
                  ) : (
                    <Skeleton height={50} width="100%" />
                  )}
                </Grid>

                <Grid item sm={6} xs={12}>
                  <SectionTitle icon={faPhone} label={translate('subscription.yourParticulars')} />

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        error={!!formState.errors.email}
                        helperText={formState.errors.email}
                        id="email"
                        label={translate('common.email')}
                        required
                        variant="outlined"
                        {...email('email')}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        error={!!formState.errors.phone}
                        helperText={formState.errors.phone}
                        id="phone"
                        label={translate('common.phone')}
                        variant="outlined"
                        {...tel('phone')}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>

      {/* SECTION 2 - Abonnement */}
      <Grid item xs={12} sm={6}>
        <SectionHeader
          label={translate('subscription.subscriptionInfos')}
          sectionNumber="2"
          onClick={(currentSectionOpen !== 2 && sectionsAlreadyOpen.includes(2)) ? () => handleSectionOpenChange(2) : null}
        />

        {currentSectionOpen === 2 && (
          <Grid container direction="column" spacing={3} style={{ marginTop: 8 }}>

            {/* Magazine + nombre d'exemplaires */}
            <Grid item>
              <Grid container spacing={3} alignItems="center">

                <Grid item xs={12} sm={4}>
                  <FormSelect
                    fullWidth
                    id="magazine"
                    label="subscription.selectMagazine"
                    name="magazine"
                    options={MAGAZINE}
                    required
                    variant="outlined"
                    {...select('magazine')}
                  />
                </Grid>

                <Grid item xs={12} sm={8}>
                  <FormLabel required style={{ marginBottom: 8 }}>
                    {translate('subscription.numberOfCopies')}
                  </FormLabel>

                  <Grid container alignItems="center" spacing={2} wrap="nowrap">
                    <Grid item>
                      <FontAwesomeIcon
                        icon={faMinusCircle}
                        size="2x"
                        style={{
                          color: formState.values.numberOfCopies > 1 ? 'var(--secondary-color)' : 'var(--grey)',
                          cursor: formState.values.numberOfCopies > 1 ? 'pointer' : 'not-allowed'
                        }}
                        onClick={formState.values.numberOfCopies > 1 ? handleMinusSubscription : undefined}
                      />
                    </Grid>

                    <Grid item>
                      <TextField
                        type="tel"
                        variant="outlined"
                        inputProps={{ style: { textAlign: 'center' } }}
                        style={{ width: 75 }}
                        id="numberOfCopies"
                        {...number('numberOfCopies')}
                      />
                    </Grid>

                    <Grid item>
                      <FontAwesomeIcon
                        icon={faPlusCircle}
                        size="2x"
                        style={{ color: 'var(--secondary-color)', cursor: 'pointer' }}
                        onClick={handlePlusSubscription}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Souscriptions */}
            {formState.values.subscriptions.map((subscription, index) => (
              <Grid item key={index}>
                <Subscription
                  formState={formState}
                  prices={prices}
                  subscriptionData={subscription.values ? { ...subscription.values } : { ...subscription }}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Grid>

      {/* SECTION 3 - Récapitulatif */}
      <Grid item>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <SectionHeader
              label={translate('subscription.subscriptionSummary')}
              sectionNumber="3"
              onClick={(currentSectionOpen !== 3 && sectionsAlreadyOpen.includes(3)) ? () => handleSectionOpenChange(3) : null}
            />
          </Grid>

          {currentSectionOpen === 3 && (
            <Grid item xs={12}>
              <SubscriptionSummary
                checkbox={checkbox}
                formState={formState}
                prices={prices}
                setTotalPrice={setTotalPrice}
              />
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Grid container justifyContent="flex-end">
          <Grid item>

            {currentSectionOpen !== 3 ? (
              <Button
                color="secondary"
                disabled={!canGoNext}
                style={{ width: '100%', maxWidth: 250 }}
                onClick={() => handleSectionOpenChange(0)}
              >
                <Grid container justifyContent="center" spacing={1} alignItems="center">
                  <Grid item>{translate('subscription.continue')}</Grid>
                  <Grid item>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Grid>
                </Grid>
              </Button>
            ) : (
              <PayboxPayment
                buttonLabel="subscription.pay"
                disabled={!canGoNext || !formState.values.conditionsAccepted}
                getPaymentInformations={createSubscriberAndPaymentInfos}
                providedEmail={formState.values.email}
                typeId={PAYMENT_TYPE.SUBSCRIPTION.code}
              />
            )}

          </Grid>
        </Grid>
      </Grid>

    </Grid>
  );
};

export default PageSubscription;